'use client'

import VideoThumbnail from '@/components/content/videos/VideoThumbnail'
import VideoPlayer from '@/components/content/videos/VideoPlayer'
import { useMemo, useState, useEffect, useRef } from 'react'
import { filterUniqueAndLongVideos } from '@/components/content/videos/helpers'
import { isPopulatedObject, scrollToSection, timeToSeconds } from '@/lib/utils'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { usePageContext } from '@/components/content/common/PageContext'
import type { App, Video, VideoAppsApp } from '@/payload-types'

type VideosProps = {
  videos: Video[]
  videoAppsMap?: Record<number, VideoAppsApp[]>
}

export default function Videos({ videos, videoAppsMap }: VideosProps) {
  const { playVideo } = usePageContext()
  const { md } = useBreakpoint()

  const [selectedVideo, setSelectedVideo] = useState<Video | null>(videos[0] ?? null)
  const [selectedStart, setSelectedStart] = useState<number | null>(null)
  const [shouldAutoplay, setShouldAutoplay] = useState(false)
  const [projectedVideo, setProjectedVideo] = useState<VideoAppsApp | null>(null)
  const previousVideoId = useRef<number | null>(selectedVideo?.id ?? null)

  const orderedVideos = useMemo(() => {
    if (!selectedVideo) return videos

    // Filter videos to unique source and duration > 60 seconds
    return filterUniqueAndLongVideos(videos)
  }, [videos, selectedVideo])

  const handleVideoClick = (video: Video, autoPlay?: boolean, start?: string) => {
    // Drop the previous video's hover-projection so its timestampStart/highlight
    // can't leak into the newly selected video's player. projectedVideo is only
    // ever set for the selected video (VideoThumbnail guards on isSelected), and
    // mouseLeave clears it on desktop — but touch never fires mouseLeave, so a
    // tap-to-switch would otherwise carry the prior video's marker over.
    if (video.id !== selectedVideo?.id) {
      setProjectedVideo(null)
    }
    setSelectedVideo(video)
    setShouldAutoplay(autoPlay || false)

    if (start) {
      setSelectedStart(timeToSeconds(start))
    } else {
      setSelectedStart(null)
    }
  }

  useEffect(() => {
    setShouldAutoplay(playVideo)
  }, [playVideo])

  // Reset autoplay after a short delay to prevent it from staying on
  useEffect(() => {
    if (shouldAutoplay) {
      const timer = setTimeout(() => {
        setShouldAutoplay(false)
      }, 100) // Reset after 100ms
      return () => clearTimeout(timer)
    }
  }, [shouldAutoplay])

  // Scroll to video player when selected video changes
  useEffect(() => {
    const currentVideoId = selectedVideo?.id ?? null

    if (previousVideoId.current !== currentVideoId && previousVideoId.current !== null) {
      const offset = !md ? 200 : 150
      scrollToSection({ id: 'video-player', offset, animate: true, hideHash: true })
    }

    previousVideoId.current = currentVideoId
  }, [selectedVideo, md])

  if (!selectedVideo) {
    return null
  }

  const projectedAppSlug = isPopulatedObject<App>(projectedVideo?.app) ? projectedVideo.app.slug : null

  return (
    <div className="flex flex-col gap-4">
      <VideoPlayer
        key={selectedVideo.id}
        video={selectedVideo}
        videoApps={videoAppsMap?.[selectedVideo.id]}
        timestamp={projectedVideo?.timestampStart ?? undefined}
        externalHighlightedApp={projectedAppSlug}
        defaultPlaying={false}
        autoPlay={shouldAutoplay}
        triggerStart={selectedStart}
      />

      <div className="flex flex-col">
        {orderedVideos.map((video, index) => {
          const apps = videoAppsMap?.[video.id] as VideoAppsApp[]

          return (
            <VideoThumbnail
              key={video.id || index}
              video={video}
              apps={apps}
              isSelected={selectedVideo.id === video.id}
              selectVideo={(autoPlay, start) => handleVideoClick(video, autoPlay, start)}
              setProjectedVideo={setProjectedVideo}
            />
          )
        })}
      </div>
    </div>
  )
}
